import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '搜索', to: '/search' },
  { label: '关于', to: '/about' },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScroll = useRef(0);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > 140 && y > lastScroll.current);
      lastScroll.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (location.pathname === '/search') {
      const q = new URLSearchParams(location.search).get('q') || '';
      setKeyword(q);
    }
  }, [location.pathname, location.search]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) {
      navigate('/search');
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <>
    <header
      className={`sticky top-0 z-50 bg-primary-950 text-background-50 transition-transform duration-300 ${
        hidden ? '-translate-y-full' : 'translate-y-0'
      }`}
    >
      <div className="flex items-center gap-4 px-4 md:px-8 h-16">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-500 text-background-50">
            <i className="ri-film-line text-xl"></i>
          </span>
          <span className="font-heading font-black text-lg tracking-wide whitespace-nowrap">
            诱人的女员工<span className="text-accent-500">hd</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-6">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'text-accent-400 bg-primary-900'
                    : 'text-background-200 hover:text-background-50 hover:bg-primary-900'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="hidden md:flex items-center ml-auto bg-primary-900 rounded-full px-3 h-10 w-full max-w-xs"
        >
          <span className="w-6 h-6 flex items-center justify-center text-background-300">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索影视名、演员…"
            className="flex-1 bg-transparent outline-none text-sm px-2 placeholder:text-background-400"
          />
          <button
            type="submit"
            className="text-xs font-medium text-accent-400 hover:text-accent-300 whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setDrawerOpen(true)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md hover:bg-primary-900 cursor-pointer"
        >
          <i className="ri-menu-line text-2xl"></i>
        </button>
      </div>
    </header>

      {/* mobile drawer */}
      <div
        className={`md:hidden fixed inset-0 z-50 transition-opacity ${
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setDrawerOpen(false)}
        ></div>
        <aside
          className={`absolute top-0 right-0 h-full w-72 max-w-[80%] bg-primary-950 px-5 py-6 transition-transform duration-300 ${
            drawerOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <span className="font-heading font-black text-base">导航菜单</span>
            <button
              type="button"
              aria-label="关闭菜单"
              onClick={() => setDrawerOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-primary-900 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <form onSubmit={submitSearch} className="flex items-center bg-primary-900 rounded-full px-3 h-10 mb-5">
            <span className="w-6 h-6 flex items-center justify-center text-background-300">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视名、演员…"
              className="flex-1 bg-transparent outline-none text-sm px-2 placeholder:text-background-400"
            />
          </form>
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm transition-colors cursor-pointer ${
                    isActive ? 'bg-primary-900 text-accent-400' : 'text-background-200 hover:bg-primary-900'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
      </div>
    </>
  );
}